// apps/admin/src/services/apiClient.ts

export const API_URL = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? 'http://localhost:8001' : '');

export interface UploadResult {
  path: string;
  url: string;
}

function getToken(): string | null {
  return localStorage.getItem('access_token');
}

function setToken(token: string): void {
  localStorage.setItem('access_token', token);
}

function clearToken(): void {
  localStorage.removeItem('access_token');
}

function authHeaders(): HeadersInit {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function errorMessage(detail: unknown, fallback: string): string {
  if (typeof detail === 'string' && detail) return detail;
  if (detail && typeof detail === 'object') {
    const conflicts = (detail as { conflicts?: unknown }).conflicts;
    if (Array.isArray(conflicts) && conflicts.length) {
      return conflicts.map((conflict) => {
        if (!conflict || typeof conflict !== 'object') return String(conflict);
        const { code, change_id: changeId } = conflict as { code?: unknown; change_id?: unknown };
        const label = typeof code === 'string' ? code.replaceAll('_', ' ') : 'publication conflict';
        return changeId ? `${label} (change ${String(changeId)})` : label;
      }).join('; ');
    }
    try { return JSON.stringify(detail); } catch { /* fall through */ }
  }
  return fallback;
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...options.headers,
    },
  });

  if (res.status === 401) {
    clearToken();
    window.dispatchEvent(new CustomEvent('auth:expired'));
    throw new Error('Session expired');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(errorMessage(body.detail ?? body.error, `HTTP ${res.status}`));
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const apiClient = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown, headers?: HeadersInit) => request<T>(path, { method: 'POST', body: JSON.stringify(body), headers }),
  patch: <T>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),

  downloadBlob: async (path: string, filename: string): Promise<void> => {
    const res = await fetch(`${API_URL}${path}`, { headers: authHeaders() });
    if (res.status === 401) {
      clearToken();
      window.dispatchEvent(new CustomEvent('auth:expired'));
      throw new Error('Session expired');
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const url = URL.createObjectURL(await res.blob());
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },

  /** Multipart file upload — does NOT set Content-Type (browser sets it with boundary) */
  upload: async <T = UploadResult>(path: string, formData: FormData): Promise<T> => {
    const token = getToken();
    const res = await fetch(`${API_URL}${path}`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(errorMessage(body.detail, `Upload failed: ${res.status}`));
    }
    return res.json() as Promise<T>;
  },

  setToken,
  getToken,
  clearToken,
};

export default apiClient;
